/**
 * Format date to show only day.month.year format without time
 * @param {string|Date} dateTime - The date to format
 * @returns {string} Formatted date in DD.MM.YYYY format or '-' if invalid
 */
export const formatDateOnly = (dateTime) => {
  if (!dateTime || dateTime === "0") return '-';
  
  const dateStr = String(dateTime).trim();
  
  // Handle DDMMYYYY format (like 23122025)
  if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    const day = dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const year = dateStr.substring(4, 8);
    
    const date = new Date(`${year}-${month}-${day}`);
    
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  }
  
  // Handle backend date format (YYYYMMDDHHMMSS or YYYYMMDDHHMMSS000)
  if (dateStr.length >= 14) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    
    const date = new Date(`${year}-${month}-${day}`);
    
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  }
  
  // Handle standard date objects or ISO strings
  try {
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  } catch (error) {
    console.error('Date formatting error:', error);
  }
  
  return '-';
};

/**
 * Format date with time (existing functionality for backward compatibility)
 * @param {string|Date} dateTime - The date to format
 * @returns {string} Formatted date with time in DD.MM.YYYY HH:MM:SS format or '-' if invalid
 */
export const formatDateWithTime = (dateTime) => {
  if (!dateTime || dateTime === "0") return '-';
  
  const dateStr = String(dateTime).trim();
  
  if (dateStr.length === 16) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const hour = dateStr.substring(8, 10);
    const minute = dateStr.substring(10, 12);
    const second = dateStr.substring(12, 14);
    
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    
    if (!isNaN(date.getTime())) {
      const utcPlus3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
      
      return utcPlus3Date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
  
  if (dateStr.length === 14) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const hour = dateStr.substring(8, 10);
    const minute = dateStr.substring(10, 12);
    const second = dateStr.substring(12, 14);
    
    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    
    if (!isNaN(date.getTime())) {
      const utcPlus3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
      
      return utcPlus3Date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
  
  try {
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  } catch (error) {
    console.error('Date formatting error:', error);
  }
  
  return '-';
}; 

/**
 * Convert date to DDMMYYYY format for backend submission
 * @param {string|Date} dateTime - The date to convert
 * @returns {string} Date in DDMMYYYY format or empty string if invalid
 */
export const formatDateForBackend = (dateTime) => {
  if (!dateTime || dateTime === "0") return '';
  
  const dateStr = String(dateTime).trim();
  
  // Handle DDMMYYYY format (like 23122025) - return as is
  if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Handle YYYY-MM-DD format (HTML date input format)
  if (dateStr.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(5, 7);
    const day = dateStr.substring(8, 10);
    return `${day}${month}${year}`;
  }
  
  // Handle backend date format (YYYYMMDDHHMMSS or YYYYMMDDHHMMSS000)
  if (dateStr.length >= 14) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}${month}${year}`;
  }
  
  // Handle standard date objects or ISO strings
  try {
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear());
      return `${day}${month}${year}`;
    }
  } catch (error) {
    console.error('Date formatting error:', error);
  }
  
  return '';
};

/**
 * Convert date to YYYY-MM-DD format for HTML date input
 * @param {string|Date} dateTime - The date to convert
 * @returns {string} Date in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateForInput = (dateTime) => {
  if (!dateTime || dateTime === "0") return '';
  
  const dateStr = String(dateTime).trim();
  
  // Handle YYYY-MM-DD format (HTML date input format) - return as is
  if (dateStr.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Handle DDMMYYYY format (like 23122025)
  if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    const day = dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const year = dateStr.substring(4, 8);
    return `${year}-${month}-${day}`;
  }
  
  // Handle backend date format (YYYYMMDDHHMMSS or YYYYMMDDHHMMSS000)
  if (dateStr.length >= 14) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}`;
  }
  
  // Handle standard date objects or ISO strings
  try {
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (error) {
    console.error('Date formatting error:', error);
  }
  
  return '';
};

/**
 * Check if a date falls within a date range
 * @param {string|Date} itemDate - The date to check
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {boolean} True if date is within range
 */
export const isDateInRange = (itemDate, startDate, endDate) => {
  if (!itemDate || !startDate || !endDate) return true;
  
  try {
    // Convert item date to comparable format
    let itemDateStr = String(itemDate).trim();
    let itemDateObj;
    
    // Handle DDMMYYYY format (like 23122025)
    if (itemDateStr.length === 8 && /^\d{8}$/.test(itemDateStr)) {
      const day = itemDateStr.substring(0, 2);
      const month = itemDateStr.substring(2, 4);
      const year = itemDateStr.substring(4, 8);
      itemDateObj = new Date(`${year}-${month}-${day}`);
    }
    // Handle backend date format (YYYYMMDDHHMMSS or YYYYMMDDHHMMSS000)
    else if (itemDateStr.length >= 14) {
      const year = itemDateStr.substring(0, 4);
      const month = itemDateStr.substring(4, 6);
      const day = itemDateStr.substring(6, 8);
      itemDateObj = new Date(`${year}-${month}-${day}`);
    }
    // Handle standard date objects or ISO strings
    else {
      itemDateObj = new Date(itemDate);
    }
    
    if (isNaN(itemDateObj.getTime())) return true;
    
    // Convert start and end dates to Date objects
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) return true;
    
    // Set time to start of day for accurate comparison
    itemDateObj.setHours(0, 0, 0, 0);
    startDateObj.setHours(0, 0, 0, 0);
    endDateObj.setHours(23, 59, 59, 999);
    
    return itemDateObj >= startDateObj && itemDateObj <= endDateObj;
  } catch (error) {
    console.error('Date range filtering error:', error);
    return true;
  }
}; 